// src/pages/Tickets/TicketList.jsx
// -----------------------------------------------------------------------------
// Lists all tickets and provides full CRUD capabilities using reusable components.
// Performs full CRUD (Read, Create, Update, Delete) locally or via ticketService.
// -----------------------------------------------------------------------------
import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ticketService, { mockSeverities, mockStatuses, mockEnvironments } from '../../api/ticketService';
import userService from '../../api/userService';
import ticketAssignmentService from '../../api/ticketAssignmentService';
import clientQuestionService from '../../api/clientQuestionService';
import { buildStatusHelpers } from '../../utils/ticketStatus';
import useTableData from '../../hooks/useTableData';
import useTicketFormOptions from '../../hooks/useTicketFormOptions';
import TableList from '../../components/common/TableList';
import TableForm from '../../components/common/TableForm';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';
import Modal from '../../components/common/Modal';
import StatusBadge from '../../components/common/StatusBadge';
import { useAuth } from '../../context/AuthContext';

// Helper to extract full user name from any user object shape
const getUserFullName = (u) => {
  if (!u) return '';
  if (typeof u === 'string' || typeof u === 'number') return String(u);
  const first = (u.first_Name || u.firstName || u.First_Name || '').trim();
  const last = (u.last_Name || u.lastName || u.Last_Name || '').trim();
  const full = `${first} ${last}`.trim();
  if (full) return full;
  if (u.fullName || u.name || u.userName || u.user_Name) {
    return (u.fullName || u.name || u.userName || u.user_Name).trim();
  }
  if (u.email || u.Email) return u.email || u.Email;
  const uid = u.id ?? u.userID ?? u.userId ?? u.Id ?? u.UserId;
  return uid ? `User ${uid}` : 'User';
};

// Helper to extract User ID
const getUserId = (u) => {
  if (!u) return '';
  return u.id ?? u.userID ?? u.userId ?? u.Id ?? u.UserId ?? '';
};

export default function TicketList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Initialize CRUD Hook
  const {
    data,
    loading,
    submitting,
    deleting,
    serverErrors,
    searchTerm,
    setSearchTerm,
    sortConfig,
    handleSort,
    isFormOpen,
    isDeleteOpen,
    formMode,
    selectedRecord,
    openCreateModal,
    openEditModal,
    closeFormModal,
    openDeleteModal,
    closeDeleteModal,
    handleSave,
    handleDelete,
  } = useTableData(ticketService, {
    idKey: 'ticketID',
    itemName: 'Ticket',
    initialSort: { key: 'ticketID', direction: 'desc' },
  });

  // Use the dedicated cascading forms hook
  const {
    projects = [],
    phases = [],
    ticketTypes = [],
    allSubTypes = [],
    environments = [],
    severities = [],
    statuses = [],
    filteredPhases = [],
    filteredSubTypes = [],
    filteredEnvironments = [],
    filteredSeverities = [],
    filteredStatuses = [],
    currentFormValues = {},
    handleFieldChange,
  } = useTicketFormOptions(selectedRecord);

  const { user, hasRole } = useAuth();

  // Requirement #18: the selected project's client-facing questions are shown
  // when raising a ticket; mandatory ones are required for Client users.
  const [clientQuestions, setClientQuestions] = useState([]);
  useEffect(() => {
    clientQuestionService
      .getAll()
      .then((res) => setClientQuestions(res.data || []))
      .catch(() => setClientQuestions([]));
  }, []);
  const [users, setUsers] = useState([]);
  const [allAssignments, setAllAssignments] = useState([]);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [ticketToAssign, setTicketToAssign] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [assigning, setAssigning] = useState(false);

  // Load all assignments and users for table & assign modal
  const loadAssignmentsAndUsers = () => {
    userService.getAll()
      .then((res) => {
        const raw = res?.data?.data || res?.data || [];
        setUsers(Array.isArray(raw) ? raw : []);
      })
      .catch(() => {});

    ticketAssignmentService.getAll()
      .then((res) => setAllAssignments(res.data || []))
      .catch(() => {});
  };

  useEffect(() => {
    loadAssignmentsAndUsers();
  }, []);

  const openAssignTicketModal = (ticket) => {
    setTicketToAssign(ticket);
    const existing = (allAssignments || []).find(
      (a) => Number(a.ticketID) === Number(ticket.ticketID) && a.isCurrentFlag
    );
    setSelectedUserId(existing ? String(existing.userID || existing.userId) : '');
    setIsAssignModalOpen(true);
  };

  const handleAssignTicketSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!ticketToAssign) return;
    if (!selectedUserId) {
      toast.warning('Please select a user to assign.');
      return;
    }

    const alreadyAssigned = (allAssignments || []).some(
      (a) => Number(a.ticketID) === Number(ticketToAssign.ticketID) && a.isCurrentFlag && Number(a.userID || a.userId) === Number(selectedUserId)
    );
    if (alreadyAssigned) {
      toast.warning('This user is already assigned to this ticket.');
      return;
    }

    setAssigning(true);
    try {
      const loggedInId = user?.userID || user?.userId || user?.id || 1;
      await ticketService.assign(ticketToAssign.ticketID, selectedUserId, loggedInId);
      toast.success(`Ticket ${ticketToAssign.ticketNumber || `#${ticketToAssign.ticketID}`} assigned successfully!`);
      setIsAssignModalOpen(false);
      setTicketToAssign(null);
      setSelectedUserId('');
      loadAssignmentsAndUsers();
    } catch (err) {
      console.error('Failed to assign ticket:', err);
      const errMsg = err?.response?.data?.message || err?.response?.data?.title || err?.message || 'Failed to assign ticket.';
      toast.error(errMsg);
    } finally {
      setAssigning(false);
    }
  };

  const handleUnassignCurrent = async () => {
    if (!ticketToAssign) return;
    const existing = (allAssignments || []).find(
      (a) => Number(a.ticketID) === Number(ticketToAssign.ticketID) && a.isCurrentFlag
    );
    if (!existing) return;
    setAssigning(true);
    try {
      await ticketService.unassign(ticketToAssign.ticketID, existing.assignmentID);
      toast.success('User unassigned from ticket.');
      setIsAssignModalOpen(false);
      setTicketToAssign(null);
      setSelectedUserId('');
      loadAssignmentsAndUsers();
    } catch (err) {
      console.error('Failed to unassign user:', err);
      toast.error('Failed to unassign user.');
    } finally {
      setAssigning(false);
    }
  };

  // Get status, severity, date, and dashboard special filters from URL
  const statusParam = searchParams.get('statusID') || searchParams.get('status');
  const severityParam = searchParams.get('severityID') || searchParams.get('severity');
  const dateParam = searchParams.get('date') || searchParams.get('createDate') || searchParams.get('createdDate');
  const filterParam = searchParams.get('filter');

  const [selectedStatusFilter, setSelectedStatusFilter] = useState(statusParam || '');
  const [selectedSeverityFilter, setSelectedSeverityFilter] = useState(severityParam || '');
  // Additional Req 7: filter by ticket type, assignee and created-date range
  const [selectedTypeFilter, setSelectedTypeFilter] = useState('');
  const [selectedProjectFilter, setSelectedProjectFilter] = useState('');
  const [selectedAssigneeFilter, setSelectedAssigneeFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [selectedDateFilter, setSelectedDateFilter] = useState(dateParam || '');
  const [activeSpecialFilter, setActiveSpecialFilter] = useState(filterParam || '');

  // Local pagination states (to properly support custom filters)
  const [localCurrentPage, setLocalCurrentPage] = useState(1);
  const [localPageSize, setLocalPageSize] = useState(10);

  // Sync filters if URL search params change
  useEffect(() => {
    if (severityParam) {
      const effSeverities = severities && severities.length > 0 ? severities : mockSeverities;
      const match = effSeverities.find(
        s => String(s.severityID) === String(severityParam) || s.severityName?.toLowerCase() === String(severityParam).toLowerCase()
      );
      setSelectedSeverityFilter(match ? String(match.severityID) : severityParam);
    } else {
      setSelectedSeverityFilter('');
    }

    if (statusParam) {
      const effStatuses = statuses && statuses.length > 0 ? statuses : mockStatuses;
      const match = effStatuses.find(
        s => String(s.statusID) === String(statusParam) || s.statusName?.toLowerCase() === String(statusParam).toLowerCase()
      );
      setSelectedStatusFilter(match ? String(match.statusID) : statusParam);
    } else {
      setSelectedStatusFilter('');
    }

    setSelectedDateFilter(dateParam || '');
    setActiveSpecialFilter(filterParam || '');
  }, [statusParam, severityParam, dateParam, filterParam, severities, statuses]);

  // Filter tickets locally
  const filteredData = useMemo(() => {
    let result = data || [];

    // Apply dashboard special filters (same per-project status rules as the Dashboard)
    const statusHelpers = buildStatusHelpers(statuses || []);
    if (activeSpecialFilter === 'client_replies') {
      result = result.filter((item) => item.clientOrgID && !statusHelpers.isClosed(item));
    } else if (activeSpecialFilter === 'staff_replies') {
      result = result.filter((item) => !statusHelpers.isNew(item));
    } else if (activeSpecialFilter === 'without_reply') {
      result = result.filter((item) => statusHelpers.isNew(item));
    } else if (activeSpecialFilter === 'client_unassigned') {
      // Requirement #21: client-raised tickets waiting for the PM to assign them
      const clientUserIds = new Set(
        (users || [])
          .filter((u) => u.client_Org_ID || u.clientOrgID || u.clientOrgId)
          .map((u) => Number(u.id ?? u.userID ?? u.userId))
      );
      result = result.filter(
        (item) =>
          clientUserIds.has(Number(item.raisedBy)) &&
          !(allAssignments || []).some((a) => Number(a.ticketID) === Number(item.ticketID) && a.isCurrentFlag)
      );
    } else if (activeSpecialFilter === 'resolved') {
      result = result.filter((item) => statusHelpers.isClosed(item));
    }

    if (selectedStatusFilter) {
      result = result.filter((item) => {
        const idMatch = Number(item.statusID) === Number(selectedStatusFilter);
        const nameMatch = String(item.statusName || '').trim().toLowerCase() === String(selectedStatusFilter).trim().toLowerCase();
        return idMatch || nameMatch;
      });
    }
    if (selectedProjectFilter) {
      result = result.filter((item) => Number(item.projectID) === Number(selectedProjectFilter));
    }
    if (selectedTypeFilter) {
      result = result.filter((item) => Number(item.ticketTypeID) === Number(selectedTypeFilter));
    }
    if (selectedAssigneeFilter) {
      result = result.filter((item) =>
        (allAssignments || []).some(
          (a) => Number(a.ticketID) === Number(item.ticketID) && a.isCurrentFlag && Number(a.userID ?? a.userId) === Number(selectedAssigneeFilter)
        )
      );
    }
    if (dateFromFilter || dateToFilter) {
      result = result.filter((item) => {
        if (!item.createDate) return false;
        const created = String(new Date(item.createDate).toISOString()).slice(0, 10);
        return (!dateFromFilter || created >= dateFromFilter) && (!dateToFilter || created <= dateToFilter);
      });
    }
    if (selectedSeverityFilter) {
      result = result.filter((item) => {
        const idMatch = Number(item.severityID) === Number(selectedSeverityFilter);
        const nameMatch = String(item.severityName || '').trim().toLowerCase() === String(selectedSeverityFilter).trim().toLowerCase();
        return idMatch || nameMatch;
      });
    }
    if (selectedDateFilter) {
      result = result.filter((item) => {
        if (!item.createDate) return false;
        try {
          const cDate = new Date(item.createDate);
          if (isNaN(cDate.getTime())) return false;

          const y = cDate.getFullYear();
          const m = String(cDate.getMonth() + 1).padStart(2, '0');
          const d = String(cDate.getDate()).padStart(2, '0');
          const localYMD = `${y}-${m}-${d}`;
          const isoYMD = cDate.toISOString().slice(0, 10);

          let targetYMD = String(selectedDateFilter).trim();
          if (targetYMD.includes('T')) {
            targetYMD = targetYMD.slice(0, 10);
          } else if (targetYMD.length > 10) {
            const targetDateObj = new Date(targetYMD);
            if (!isNaN(targetDateObj.getTime())) {
              targetYMD = targetDateObj.toISOString().slice(0, 10);
            }
          }
          return localYMD === targetYMD || isoYMD === targetYMD;
        } catch (e) {
          return false;
        }
      });
    }
    return result;
  }, [data, activeSpecialFilter, selectedStatusFilter, selectedSeverityFilter, selectedDateFilter, users, allAssignments, selectedTypeFilter, selectedAssigneeFilter, dateFromFilter, dateToFilter, statuses, selectedProjectFilter]);

  // Slice for current page
  const paginatedTickets = useMemo(() => {
    const startIndex = (localCurrentPage - 1) * localPageSize;
    return filteredData.slice(startIndex, startIndex + localPageSize);
  }, [filteredData, localCurrentPage, localPageSize]);

  const localTotalPages = Math.ceil(filteredData.length / localPageSize) || 1;
  const localTotalRecords = filteredData.length;

  // Reset pagination if search or filters change
  useEffect(() => {
    setLocalCurrentPage(1);
  }, [searchTerm, activeSpecialFilter, selectedStatusFilter, selectedSeverityFilter, selectedDateFilter]);

  // Open create modal if URL search params have openCreate=true
  useEffect(() => {
    if (searchParams.get('openCreate') === 'true') {
      openCreateModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Schema configuration for Ticket_TBL
  const ticketFields = useMemo(() => {
    const isEditMode = formMode === 'edit';
    const list = [];

    // Only show Ticket # in edit mode
    if (isEditMode) {
      list.push({
        name: 'ticketNumber',
        label: 'Ticket #',
        type: 'varchar',
        readOnly: true,
        placeholder: 'PROJ-123 (System Generated)',
      });
    }

    const effPhases = filteredPhases && filteredPhases.length > 0 ? filteredPhases : phases;
    const effSubTypes = filteredSubTypes && filteredSubTypes.length > 0 ? filteredSubTypes : allSubTypes;
    const effSeverities = filteredSeverities && filteredSeverities.length > 0 ? filteredSeverities : (severities && severities.length > 0 ? severities : mockSeverities);
    const effStatuses = filteredStatuses && filteredStatuses.length > 0 ? filteredStatuses : (statuses && statuses.length > 0 ? statuses : mockStatuses);
    const effEnvironments = filteredEnvironments && filteredEnvironments.length > 0 ? filteredEnvironments : (environments && environments.length > 0 ? environments : mockEnvironments);

    list.push(
      {
        name: 'projectID',
        label: 'Project',
        type: 'select',
        required: true,
        options: (projects || []).map((p) => ({ label: `${p.projectCode} — ${p.projectName}`, value: p.projectID })),
      },
      {
        name: 'phaseID',
        label: 'Phase',
        type: 'select',
        required: true,
        options: (effPhases || []).map((ph) => ({ label: ph.phaseName, value: ph.phaseID })),
      },
      {
        name: 'ticketTypeID',
        label: 'Ticket Type',
        type: 'select',
        required: true,
        options: (ticketTypes || []).map((t) => ({ label: t.typeName, value: t.ticketTypeID })),
      },
      {
        name: 'ticketSubTypeID',
        label: 'Sub-Type',
        type: 'select',
        options: (effSubTypes || []).map((sub) => ({ label: sub.subTypeName, value: sub.subTypeID })),
      },
      {
        name: 'title',
        label: 'Title',
        type: 'varchar',
        required: true,
        gridSpan: 'full',
        maxLength: 200,
        placeholder: 'Summarize the issue briefly...',
      },
      {
        name: 'description',
        label: 'Description',
        type: 'textarea',
        required: true,
        maxLength: 1000,
        placeholder: 'Describe the bug, task, or user story...',
      },
      {
        name: 'detailedDescription',
        label: 'Detailed Description',
        type: 'textarea',
        placeholder: 'Add steps to reproduce, actual vs expected results...',
      },
      {
        name: 'severityID',
        label: 'Severity',
        type: 'select',
        required: true,
        options: (effSeverities || []).map((s) => ({ label: s.severityName, value: s.severityID })),
      }
    );

    // Only allow editing status in edit mode (defaults to Open on creation)
    if (isEditMode) {
      list.push({
        name: 'statusID',
        label: 'Status',
        type: 'select',
        required: true,
        options: (effStatuses || []).map((st) => ({ label: st.statusName, value: st.statusID })),
      });
    }

    list.push({
      name: 'environmentID',
      label: 'Environment',
      type: 'select',
      options: (effEnvironments || []).map((env) => ({ label: env.environmentName, value: env.environmentID })),
    });

    // Client-facing questions of the selected project - shown only to Client
    // users raising a ticket (Requirement #18). Field names
    // "clientAnswer_<questionID>" are turned into clientAnswers on save.
    if (!isEditMode && currentFormValues?.projectID && hasRole('Client')) {
      clientQuestions
        .filter((q) => Number(q.projectID) === Number(currentFormValues.projectID))
        .sort((a, b) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0))
        .forEach((q) => {
          list.push({
            name: `clientAnswer_${q.questionID}`,
            label: q.questionText,
            type: 'textarea',
            maxLength: 1000,
            required: !!q.isMandatoryFlag,
          });
        });
    }

    return list;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, phases, filteredPhases, ticketTypes, allSubTypes, filteredSubTypes, severities, filteredSeverities, statuses, filteredStatuses, environments, filteredEnvironments, formMode, clientQuestions, currentFormValues?.projectID]);

  const columns = [
    { key: 'ticketID', label: 'ID', sortable: true, width: '70px' },
    { key: 'ticketNumber', label: 'Ticket ', sortable: true, width: '100px' },
    { key: 'title', label: 'Title', sortable: true },
    {
      key: 'projectID',
      label: 'Project',
      sortable: true,
      render: (row) => {
        const proj = (projects || []).find((p) => Number(p.projectID) === Number(row.projectID));
        return proj ? proj.projectName : '-';
      },
    },
    {
      key: 'ticketTypeID',
      label: 'Type',
      sortable: true,
      render: (row) => {
        const type = (ticketTypes || []).find((t) => Number(t.ticketTypeID) === Number(row.ticketTypeID));
        return type ? type.typeName : '-';
      },
    },
    {
      key: 'severityID',
      label: 'Severity',
      sortable: true,
      render: (row) => {
        const sev = (severities || []).find((s) => Number(s.severityID) === Number(row.severityID))
          || mockSeverities.find((s) => Number(s.severityID) === Number(row.severityID));
        return sev ? <StatusBadge value={sev.severityName} /> : '-';
      },
    },
    {
      key: 'statusID',
      label: 'Status',
      sortable: true,
      render: (row) => {
        const st = (statuses || []).find((s) => Number(s.statusID) === Number(row.statusID))
          || mockStatuses.find((s) => Number(s.statusID) === Number(row.statusID));
        return st ? <StatusBadge value={st.statusName} /> : '-';
      },
    },
    {
      key: 'assignee',
      label: 'Assignee',
      sortable: false,
      render: (row) => {
        const ticketAssignments = allAssignments.filter(
          (a) => Number(a.ticketID) === Number(row.ticketID) && a.isCurrentFlag
        );
        if (ticketAssignments.length > 0) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              {ticketAssignments.map((a) => {
                const uid = a.userID || a.userId;
                const userObj = users.find((u) => Number(getUserId(u)) === Number(uid));
                const uname = userObj ? getUserFullName(userObj) : (a.userName && !a.userName.startsWith('User ') ? a.userName : (uid ? `User ${uid}` : ''));
                return (
                  <span
                    key={a.assignmentID}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '3px 8px',
                      borderRadius: '12px',
                      fontSize: '11.5px',
                      fontWeight: '600',
                      background: 'rgba(249, 115, 22, 0.1)',
                      color: 'var(--afix-orange-dark)',
                      border: '1px solid rgba(249, 115, 22, 0.25)'
                    }}
                    title={`Assigned to ${uname} (ID: ${uid})`}
                  >
                    👤 {uname} {uid ? `(ID: ${uid})` : ''}
                  </span>
                );
              })}
            </div>
          );
        }
        return (
          <span style={{ color: 'var(--color-text-muted)', fontSize: '12.5px' }}>
            Unassigned
          </span>
        );
      },
    },
    {
      key: 'createDate',
      label: 'Created Date',
      sortable: true,
      render: (row) => {
        if (!row.createDate) return '-';
        const d = new Date(row.createDate);
        return isNaN(d.getTime()) ? row.createDate : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      },
    },
  ];

  const renderHeaderActions = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      {/* Status Filter */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <label htmlFor="statusFilterSelect" style={{ fontWeight: '500', fontSize: '13px', margin: 0, whiteSpace: 'nowrap' }}>
          Status:
        </label>
        <select
          id="statusFilterSelect"
          value={selectedStatusFilter}
          onChange={(e) => setSelectedStatusFilter(e.target.value)}
          style={{
            padding: '6px 12px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--color-border)',
            fontSize: '13px',
            backgroundColor: 'var(--color-bg)',
            color: 'var(--color-text)',
            minWidth: '120px'
          }}
        >
          <option value="">All Statuses</option>
          {((statuses && statuses.length > 0) ? statuses : mockStatuses).map((s) => (
            <option key={s.statusID} value={s.statusID}>
              {s.statusName}
            </option>
          ))}
        </select>
      </div>

      {/* Severity Filter */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <label htmlFor="severityFilterSelect" style={{ fontWeight: '500', fontSize: '13px', margin: 0, whiteSpace: 'nowrap' }}>
          Severity:
        </label>
        <select
          id="severityFilterSelect"
          value={selectedSeverityFilter}
          onChange={(e) => setSelectedSeverityFilter(e.target.value)}
          style={{
            padding: '6px 12px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--color-border)',
            fontSize: '13px',
            backgroundColor: 'var(--color-bg)',
            color: 'var(--color-text)',
            minWidth: '120px'
          }}
        >
          <option value="">All Severities</option>
          {((severities && severities.length > 0) ? severities : mockSeverities).map((s) => (
            <option key={s.severityID} value={s.severityID}>
              {s.severityName}
            </option>
          ))}
        </select>
      </div>

      {/* Project / ticket type / assignee / created-date range filters */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
        <select
          aria-label="Filter by project"
          value={selectedProjectFilter}
          onChange={(e) => { setSelectedProjectFilter(e.target.value); setLocalCurrentPage(1); }}
          style={{ padding: '6px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-border)', fontSize: '13px' }}
        >
          <option value="">All Projects</option>
          {(projects || []).map((p) => (
            <option key={p.projectID} value={p.projectID}>{p.projectCode} — {p.projectName}</option>
          ))}
        </select>
        <select
          aria-label="Filter by ticket type"
          value={selectedTypeFilter}
          onChange={(e) => { setSelectedTypeFilter(e.target.value); setLocalCurrentPage(1); }}
          style={{ padding: '6px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-border)', fontSize: '13px' }}
        >
          <option value="">All Types</option>
          {(ticketTypes || []).map((t) => (
            <option key={t.ticketTypeID} value={t.ticketTypeID}>{t.typeName}</option>
          ))}
        </select>
        <select
          aria-label="Filter by assignee"
          value={selectedAssigneeFilter}
          onChange={(e) => { setSelectedAssigneeFilter(e.target.value); setLocalCurrentPage(1); }}
          style={{ padding: '6px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-border)', fontSize: '13px' }}
        >
          <option value="">All Assignees</option>
          {(users || []).map((u) => {
            const uid = u.id ?? u.userID ?? u.userId;
            return (
              <option key={uid} value={uid}>
                {`${u.first_Name || u.firstName || ''} ${u.last_Name || u.lastName || ''}`.trim() || u.email}
              </option>
            );
          })}
        </select>
        <label style={{ fontSize: '13px', margin: 0 }}>
          From{' '}
          <input type="date" value={dateFromFilter} onChange={(e) => { setDateFromFilter(e.target.value); setLocalCurrentPage(1); }} />
        </label>
        <label style={{ fontSize: '13px', margin: 0 }}>
          To{' '}
          <input type="date" value={dateToFilter} onChange={(e) => { setDateToFilter(e.target.value); setLocalCurrentPage(1); }} />
        </label>
      </div>

      {/* Requirement #21: queue of client-raised tickets nobody is assigned to yet */}
      {hasRole('Admin', 'Project Manager') && (
        <button
          type="button"
          className={`btn ${activeSpecialFilter === 'client_unassigned' ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => {
            setActiveSpecialFilter((f) => (f === 'client_unassigned' ? '' : 'client_unassigned'));
            setLocalCurrentPage(1);
          }}
          title="Client-raised tickets that are not assigned to anyone"
        >
          Client tickets to assign
        </button>
      )}

      {/* Date Filter */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <label htmlFor="dateFilterInput" style={{ fontWeight: '500', fontSize: '13px', margin: 0, whiteSpace: 'nowrap' }}>
          Date:
        </label>
        <input
          type="date"
          id="dateFilterInput"
          value={selectedDateFilter}
          onChange={(e) => setSelectedDateFilter(e.target.value)}
          style={{
            padding: '5px 10px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--color-border)',
            fontSize: '13px',
            backgroundColor: 'var(--color-bg)',
            color: 'var(--color-text)',
          }}
        />
      </div>

      {(selectedStatusFilter || selectedSeverityFilter || selectedDateFilter || activeSpecialFilter) && (
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => {
            setSelectedStatusFilter('');
            setSelectedSeverityFilter('');
            setSelectedDateFilter('');
            setActiveSpecialFilter('');
            navigate('/tickets');
          }}
          style={{ padding: '6px 12px', fontSize: '12px' }}
        >
          Clear All Filters
        </button>
      )}
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Tickets</h1>
          <div className="subtitle">
            Full CRUD tracking for bugs, tasks, and stories.
          </div>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>
          + New Ticket
        </button>
      </div>

      {/* Main Table list */}
      {activeSpecialFilter && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
          background: '#fff7ed',
          border: '1px solid #fed7aa',
          padding: '10px 16px',
          borderRadius: '8px',
          color: '#c2410c',
          fontSize: '13px',
          fontWeight: '500'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '15px' }}>🔍</span>
            <span>
              Filtered from Dashboard: <strong>{
                activeSpecialFilter === 'client_replies' ? 'Client Replies' :
                activeSpecialFilter === 'staff_replies' ? 'Staff Replies (In Progress / QA / Handling)' :
                activeSpecialFilter === 'without_reply' ? 'Tickets Without Reply (New / Unhandled)' :
                activeSpecialFilter === 'resolved' ? 'Resolved / Closed Tickets' : activeSpecialFilter
              }</strong> ({filteredData.length} {filteredData.length === 1 ? 'ticket' : 'tickets'} found)
            </span>
          </div>
          <button
            onClick={() => {
              setActiveSpecialFilter('');
              navigate('/tickets');
            }}
            style={{
              background: '#ea580c',
              color: '#ffffff',
              border: 'none',
              padding: '5px 12px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '600',
              boxShadow: '0 1px 3px rgba(234, 88, 12, 0.3)'
            }}
          >
            ✕ Show All Tickets
          </button>
        </div>
      )}

      {/* Date Filter Notification Banner */}
      {selectedDateFilter && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
          background: '#eff6ff',
          border: '1px solid #bfdbfe',
          padding: '10px 16px',
          borderRadius: '8px',
          color: '#1e40af',
          fontSize: '13px',
          fontWeight: '500'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '15px' }}>📅</span>
            <span>
              Showing all tickets recorded on <strong>{
                (() => {
                  try {
                    const d = new Date(selectedDateFilter);
                    return isNaN(d.getTime()) ? selectedDateFilter : d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                  } catch (e) {
                    return selectedDateFilter;
                  }
                })()
              }</strong> ({filteredData.length} {filteredData.length === 1 ? 'ticket' : 'tickets'} found)
            </span>
          </div>
          <button
            onClick={() => {
              setSelectedDateFilter('');
              navigate('/tickets');
            }}
            style={{
              background: '#2563eb',
              color: '#ffffff',
              border: 'none',
              padding: '5px 12px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '600',
              boxShadow: '0 1px 3px rgba(37, 99, 235, 0.3)'
            }}
          >
            ✕ Show All Dates
          </button>
        </div>
      )}

      {/* Severity Filter Notification Banner */}
      {selectedSeverityFilter && !activeSpecialFilter && !selectedDateFilter && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
          background: '#faf5ff',
          border: '1px solid #e9d5ff',
          padding: '10px 16px',
          borderRadius: '8px',
          color: '#7e22ce',
          fontSize: '13px',
          fontWeight: '500'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '15px' }}>⚡</span>
            <span>
              Showing all tickets with Priority: <strong>{
                ((severities && severities.length > 0) ? severities : mockSeverities).find(s => String(s.severityID) === String(selectedSeverityFilter))?.severityName || selectedSeverityFilter
              }</strong> ({filteredData.length} {filteredData.length === 1 ? 'ticket' : 'tickets'} found)
            </span>
          </div>
          <button
            onClick={() => {
              setSelectedSeverityFilter('');
              navigate('/tickets');
            }}
            style={{
              background: '#9333ea',
              color: '#ffffff',
              border: 'none',
              padding: '5px 12px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '600',
              boxShadow: '0 1px 3px rgba(147, 51, 234, 0.3)'
            }}
          >
            ✕ Show All Severities
          </button>
        </div>
      )}

      {/* Status Filter Notification Banner */}
      {selectedStatusFilter && !activeSpecialFilter && !selectedDateFilter && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
          background: '#f0fdf4',
          border: '1px solid #bbf7d0',
          padding: '10px 16px',
          borderRadius: '8px',
          color: '#15803d',
          fontSize: '13px',
          fontWeight: '500'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '15px' }}>📌</span>
            <span>
              Showing all tickets with Status: <strong>{
                ((statuses && statuses.length > 0) ? statuses : mockStatuses).find(s => String(s.statusID) === String(selectedStatusFilter))?.statusName || selectedStatusFilter
              }</strong> ({filteredData.length} {filteredData.length === 1 ? 'ticket' : 'tickets'} found)
            </span>
          </div>
          <button
            onClick={() => {
              setSelectedStatusFilter('');
              navigate('/tickets');
            }}
            style={{
              background: '#16a34a',
              color: '#ffffff',
              border: 'none',
              padding: '5px 12px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '600',
              boxShadow: '0 1px 3px rgba(22, 163, 74, 0.3)'
            }}
          >
            ✕ Show All Statuses
          </button>
        </div>
      )}

      <TableList
        columns={columns}
        data={filteredData}
        paginatedData={paginatedTickets}
        loading={loading}
        idKey="ticketID"
        emptyMessage="No tickets found."
        // Search & Filter
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        renderHeaderActions={renderHeaderActions}
        // Sorting
        sortConfig={sortConfig}
        onSort={handleSort}
        // Pagination
        currentPage={localCurrentPage}
        totalPages={localTotalPages}
        totalRecords={localTotalRecords}
        pageSize={localPageSize}
        onPageChange={setLocalCurrentPage}
        onPageSizeChange={setLocalPageSize}
        // Row Actions
        onEdit={openEditModal}
        // Deleting tickets is limited to Admin / PM (API enforces it too)
        onDelete={hasRole('Admin', 'Project Manager') ? openDeleteModal : undefined}
        renderActions={(row) => (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <button
              className="btn-table-action btn-table-action-primary"
              onClick={() => navigate(`/tickets/${row.ticketID}`)}
              title="View Ticket Details"
            >
              View
            </button>
            {!hasRole('Client') && (
              <button
                className="btn-table-action"
                onClick={() => openAssignTicketModal(row)}
                style={{ color: 'var(--afix-orange-dark)', borderColor: 'rgba(249, 115, 22, 0.4)' }}
                title="Assign Ticket"
              >
                Assign
              </button>
            )}
          </div>
        )}
      />

      {/* Form Modal for Create & Edit */}
      {isFormOpen && (
        <TableForm
          fields={ticketFields}
          initialValues={
            selectedRecord
              ? {
                  ...selectedRecord,
                  projectID: selectedRecord.projectID || '',
                  phaseID: selectedRecord.phaseID || '',
                  ticketTypeID: selectedRecord.ticketTypeID || '',
                  ticketSubTypeID: selectedRecord.ticketSubTypeID || '',
                  title: selectedRecord.title || '',
                  description: selectedRecord.description || '',
                  detailedDescription: selectedRecord.detailedDescription || '',
                  severityID: selectedRecord.severityID || '',
                  statusID: selectedRecord.statusID || '',
                  environmentID: selectedRecord.environmentID || '',
                }
              : currentFormValues || {}
          }
          mode={formMode}
          isOpen={isFormOpen}
          onSubmit={handleSave}
          onCancel={closeFormModal}
          submitting={submitting}
          serverErrors={serverErrors}
          onFieldChange={handleFieldChange}
          itemName="Ticket"
        />
      )}

      {/* Assign Ticket Modal */}
      {isAssignModalOpen && ticketToAssign && (
        <Modal
          title={`Assign Ticket — ${ticketToAssign.ticketNumber || `#${ticketToAssign.ticketID}`}`}
          onClose={() => {
            setIsAssignModalOpen(false);
            setTicketToAssign(null);
          }}
          size="medium"
        >
          <form onSubmit={handleAssignTicketSubmit}>
            <div style={{ marginBottom: '16px', padding: '12px 16px', background: 'rgba(249, 115, 22, 0.05)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(249, 115, 22, 0.2)' }}>
              <div style={{ fontWeight: '700', fontSize: '14.5px', marginBottom: '4px', color: 'var(--color-text)' }}>
                {ticketToAssign.title}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
                Ticket ID: <strong>#{ticketToAssign.ticketID}</strong> • Project: <strong>{ticketToAssign.projectName || (projects.find(p => Number(p.projectID) === Number(ticketToAssign.projectID))?.projectName) || '-'}</strong>
              </div>
              {(() => {
                const current = (allAssignments || []).find(
                  (a) => Number(a.ticketID) === Number(ticketToAssign.ticketID) && a.isCurrentFlag
                );
                return current ? (
                  <div style={{ marginTop: '8px', fontSize: '12.5px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: 'var(--color-text-muted)' }}>Current Assignee:</span>
                    <strong style={{ color: 'var(--afix-orange-dark, #ea580c)' }}>👤 {current.userName}</strong>
                    <button
                      type="button"
                      onClick={handleUnassignCurrent}
                      style={{
                        background: '#fee2e2',
                        color: '#ef4444',
                        border: '1px solid #fca5a5',
                        borderRadius: '4px',
                        padding: '2px 8px',
                        fontSize: '11px',
                        fontWeight: '700',
                        cursor: 'pointer'
                      }}
                    >
                      Unassign
                    </button>
                  </div>
                ) : (
                  <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--color-text-muted)' }}>
                    Current Assignee: <em>Unassigned</em>
                  </div>
                );
              })()}
            </div>

            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label htmlFor="modalAssignUserSelect" style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                Select Team Member to Assign <span className="required-star">*</span>
              </label>
              <select
                id="modalAssignUserSelect"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1.5px solid var(--color-border)',
                  fontSize: '14px',
                  background: 'var(--color-bg)',
                  color: 'var(--color-text)'
                }}
                required
              >
                <option value="">-- Choose team member to assign --</option>
                {users.map((u) => {
                  const uid = getUserId(u);
                  const uname = getUserFullName(u);
                  return (
                    <option key={uid} value={uid}>
                      {uname} (ID: {uid}){u.email ? ` — ${u.email}` : ''} {u.roleName || u.role ? `[${u.roleName || u.role}]` : ''}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="form-actions-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setIsAssignModalOpen(false);
                  setTicketToAssign(null);
                }}
                disabled={assigning}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={assigning || !selectedUserId}
              >
                {assigning ? 'Assigning...' : 'Assign User'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Confirmation Delete Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        title="Delete Ticket"
        itemLabel={selectedRecord ? selectedRecord.title : 'Ticket'}
        deleting={deleting}
      />
    </div>
  );
}